const DASH = '—'

/**
 * Fiyatlar virgülden sonra tek hane.
 *
 * Emir ve işlem fiyatları için kayıpsız: GİP'in fiyat adımı 0,10 TL — canlı
 * veride ölçüldü, 3375 işlem fiyatının hiçbirinde ikinci hane sıfırdan farklı
 * değil ve 219 derinlik seviyesinde yuvarlama çakışması yok.
 *
 * Tek istisna PTF: hesaplanmış bir ortalama olduğu için iki haneli olabiliyor
 * (ör. 1.400,01 → 1.400,0). Ekranda kuruş görünmüyor; karşılaştırmalar (fiyat
 * kutucuklarının renklendirmesi) yuvarlanmamış değer üzerinden yapılıyor.
 */
const priceFmt = new Intl.NumberFormat('tr-TR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})
const qtyFmt = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 })
const compactFmt = new Intl.NumberFormat('tr-TR', {
  notation: 'compact',
  maximumFractionDigits: 1,
})

export function price(value: number | null | undefined): string {
  return value == null ? DASH : priceFmt.format(value)
}

export function qty(value: number | null | undefined): string {
  return value == null ? DASH : qtyFmt.format(value)
}

export function compact(value: number | null | undefined): string {
  return value == null ? DASH : compactFmt.format(value)
}

/** 1 lot = 0,1 MWh. Lot tam sayı olduğu için bölüm daima tek ondalık verir. */
export const LOT_MWH = 0.1

const mwhFmt = new Intl.NumberFormat('tr-TR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

/** Lot → MWh. Miktarlar sistemde LOT tutuluyor, yalnız gösterimde çevriliyor. */
export function mwh(lots: number | null | undefined): string {
  return lots == null ? DASH : mwhFmt.format(lots * LOT_MWH)
}

/**
 * Unix saniye → saat. Bugünse `HH:MM:SS`, başka günse `gg.AA HH:MM`.
 *
 * Gün bilgisi şart: GİP kontratları teslimden çok önce açılıyor, bir kontratın
 * günlük eşleşme geçmişi rahatlıkla önceki güne uzanıyor. Yalnız saat
 * gösterilince liste ve grafik ekseni `18:00 → 19:29 → 08:00` gibi okunuyor.
 */
export function clock(ts: number | null | undefined): string {
  if (!ts) return DASH
  const d = new Date(ts * 1000)
  if (d.toDateString() === new Date().toDateString()) {
    return d.toLocaleTimeString('tr-TR', { hour12: false })
  }
  return d.toLocaleString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Dolum kaynağının kısa adı — kullanıcının sözlüğü (2026-07-29). Sunucuyla
 * aynı üç kelime (src/shadow/engine/models.py `LIQUIDITY_LABELS`); ikisi
 * birlikte değişmeli, yoksa log ile ekran farklı dil konuşur.
 */
export const DOLUM_ETIKETI: Record<string, string> = {
  trade: 'Simüle',       // gerçek eşleşmeden simüle edildi
  book: 'Süpürüldük',    // tahta üstümüze geldi, karşı emir bizi vurdu
  sweep: 'Süpürdük',     // tahtayı biz süpürdük
}

/** Dolum kaynağı açıklaması — tooltip'ler için uzun hâli. */
export const DOLUM_ACIKLAMA: Record<string, string> = {
  trade: 'Gerçek eşleşmeden simüle edildi',
  book: 'Tahtadan süpürüldük — karşı emir duran emrimizi vurdu',
  sweep: 'Tahtayı süpürdük — çaprazlayan emrimiz karşı tarafı tüketti',
}

export function dolumEtiketi(liquidity?: string): string {
  return DOLUM_ETIKETI[liquidity ?? 'trade'] ?? (liquidity ?? '')
}

export function dolumAciklama(liquidity?: string): string {
  return DOLUM_ACIKLAMA[liquidity ?? 'trade'] ?? ''
}

/**
 * Emrin dolum kırılımı → "6 Süpürdük · 4 Simüle". Bir emir birden çok yolla
 * dolabilir; sıralama sabit (Süpürdük, Süpürüldük, Simüle) ki satırlar arası
 * karşılaştırılabilir kalsın. Hiç dolum yoksa boş dize.
 */
export function dolumOzeti(breakdown?: Record<string, number>): string {
  if (!breakdown) return ''
  return ['sweep', 'book', 'trade']
    .filter((k) => (breakdown[k] ?? 0) > 0)
    .map((k) => `${qty(breakdown[k]!)} ${DOLUM_ETIKETI[k]}`)
    .join(' · ')
}

/** Kalan süre → HH:MM:SS. Negatifse "kapandı". */
export function countdown(msRemaining: number): string {
  if (msRemaining <= 0) return 'kapandı'
  const total = Math.floor(msRemaining / 1000)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  return [h, m, s].map((n) => String(n).padStart(2, '0')).join(':')
}
